/**
 * The PBX-neutral catalog of things a tenant can manage: people, and the call-routing features
 * built from accounts (ring groups, call queues, auto attendants, time of day rules, call park).
 *
 * Nothing here may mention vendor field names. Each adapter maps these neutral fields to its own
 * settings; the change engine, the API, the web app and Leland only ever see this catalog.
 * Values are always strings ("" = not set), which keeps comparison, undo and storage uniform.
 */

import { checkHolidays, formatHolidays, parseHolidays } from './holidays.js';
import { describeGreetings, formatGreetings, greetingProblem, parseGreetings } from './greeting.js';

import { checkKeys, formatKeys, formatMac, parseKeys, phoneModel, PHONE_MODEL_OPTIONS } from './phones.js';

export const ACCOUNT_KINDS = ['person', 'ringGroup', 'callQueue', 'autoAttendant', 'timeRule', 'callPark', 'phone', 'number', 'conference'] as const;
export type AccountKind = (typeof ACCOUNT_KINDS)[number];
/**
 * The kinds customers, Leland and the API manage. Conference rooms (1.12.18) are only Stitch's own
 * hidden presence room for busy lamps: no page, no picker, no intent can name the kind.
 */
export const CUSTOMER_KINDS = ['person', 'ringGroup', 'callQueue', 'autoAttendant', 'timeRule', 'callPark', 'phone', 'number'] as const satisfies readonly AccountKind[];
export type CustomerKind = (typeof CUSTOMER_KINDS)[number];
/** Kinds that take calls and send them on: what call flows are made of. */
export const ROUTING_KINDS = ['ringGroup', 'callQueue', 'autoAttendant', 'timeRule', 'callPark'] as const satisfies readonly AccountKind[];
export type RoutingKind = (typeof ROUTING_KINDS)[number];
export const isRoutingKind = (k: AccountKind): k is RoutingKind => (ROUTING_KINDS as readonly string[]).includes(k);

export type FieldType =
  /** Free text, such as a name. */
  | 'text'
  | 'email'
  /** A phone number for a person (mobile). */
  | 'phone'
  /** Whole seconds. */
  | 'seconds'
  /** A whole number. */
  | 'count'
  /** One extension, feature number or phone number to send calls to. */
  | 'destination'
  /** Extensions (or numbers), space-separated. */
  | 'members'
  /** "true" or "false". */
  | 'toggle'
  /** One of `options`. */
  | 'choice'
  /** Time ranges for one day, e.g. "9:00A-12:00P 1:00P-5:00P". */
  | 'hours'
  /** Spoken text (text to speech), or "file:<name>" for a recording made elsewhere. */
  | 'speech'
  /** Auto attendant keys, space-separated "key:destination" pairs, e.g. "1:201 2:300". */
  | 'menu'
  /** The number of a time of day rule in the same tenant. */
  | 'rule'
  /** Holidays and special days, as a JSON list (see Holiday). */
  | 'holidays'
  /** An uploaded recording: "sha256:<hex>" of the 8 kHz mono 16-bit WAV, or "" for none. */
  | 'recording'
  /** A short PIN: digits only. */
  | 'digits'
  /** One of a list the phone system provides (hold music, time zones). See `optionsFrom`. */
  | 'pbxChoice'
  /** A desk phone model: an id from the phone catalog, or "other:<vendor> <model>". */
  | 'phoneModel'
  /** One person's extension in the same tenant. */
  | 'person'
  /** Any account in the same tenant, by its number: what a phone number points at. */
  | 'account'
  /** A phone's programmable keys, as a JSON list (see PhoneKey). */
  | 'keys'
  /** A person's voicemail greetings, as JSON (see Greetings in greeting.ts): which plays, and their personal greetings. */
  | 'greeting';

export interface FieldOption {
  value: string;
  label: string;
  /** One line under the label, for choices that need explaining. */
  description?: string;
}

/** Lists of choices the phone system provides, per tenant. */
/** Lists the phone system provides: hold music, time zones, and the company's emergency (911) addresses. */
export type OptionList = 'holdMusic' | 'timezone' | 'location';

export interface FieldDef {
  name: string;
  /** Form label, sentence case. */
  label: string;
  /** Lower-case noun for sentences: "Update Sales: ring time 20 → 30". */
  noun: string;
  type: FieldType;
  /** Must be set when adding. */
  required?: boolean;
  help?: string;
  options?: readonly FieldOption[];
  /** Form section. */
  section?: string;
  /**
   * Only shown when another field has one of these values (e.g. the on/off switch of a rule
   * set by hand). Purely presentational: the value is still stored and compared.
   */
  showWhen?: { field: string; values: readonly string[] };
  /**
   * The setting's meaning was inferred from the vendor's admin documentation and is not yet
   * confirmed on our PBX build. Shown to staff, not customers. On a kind that is otherwise
   * confirmed, a provisional setting stays read-only until it is confirmed too.
   */
  provisional?: boolean;
  /** Shown under "More options": useful, but not needed to get started. */
  more?: boolean;
  /** For 'pbxChoice': which list the phone system provides. */
  optionsFrom?: OptionList;
  /**
   * Live state rather than configuration (who is logged in to a queue): it changes by itself,
   * so it isn't part of the settings form, but it can still be changed (and undone) like one.
   */
  live?: boolean;
  /** For toggles: what "on" means in a sentence ("rings mobile phones too"). */
  sentence?: string;
  /**
   * Changed only on its own, with a screen of its own (a person's voicemail greeting): never part
   * of adding someone, of what undoing a removal puts back, or of the settings forms.
   */
  separate?: boolean;
}

/** A starting point for "More options": a few settings that go well together. */
export interface Preset {
  id: string;
  label: string;
  description: string;
  values: FieldValues;
}

export interface KindDef {
  kind: AccountKind;
  singular: string;
  plural: string;
  /** Page and menu title. */
  title: string;
  /** Field holding the display name ("" for people, whose name is first + last). */
  nameField: string;
  fields: readonly FieldDef[];
  /** Plain-language note shown before removing one. */
  removeCaveat: string;
  presets?: readonly Preset[];
}

const NAME: FieldDef = { name: 'name', label: 'Name', noun: 'name', type: 'text', required: true };

const HOLD_MUSIC: FieldDef = {
  name: 'holdMusic',
  label: 'Hold music',
  noun: 'hold music',
  type: 'pbxChoice',
  optionsFrom: 'holdMusic',
  section: 'Sound',
  more: true,
  provisional: true,
  help: 'What callers hear while they wait. Leave on Default to use your account’s music.'
};

/** Extension feature sets. "" is the standard feature set. */
export const FEATURE_SETS: readonly FieldOption[] = [
  { value: '', label: 'Standard', description: 'Desk phone, apps, voicemail and everything else.' },
  { value: 'agent', label: 'Call queue agent', description: 'Standard, plus taking calls from call queues.' },
  { value: 'app', label: 'Apps only', description: 'Mobile and desktop apps, no desk phone.' },
  { value: 'mailbox', label: 'Voicemail only', description: 'A mailbox with no phone, for example for a department.' },
  { value: 'hotdesk', label: 'Hot desk', description: 'A shared desk phone people sign in to.' }
];

/**
 * The phone system's extension types for places only (shared phones, 3067): its own values, as its
 * admin GUI names them (`use_hotel`, `use_class`). Not offered for people; a person who has one
 * shows its name.
 */
export const PLACE_FEATURE_SETS: readonly FieldOption[] = [
  { value: 'hotel', label: 'Hotel room', description: 'Desk phones only: calls inside the company and basic outside calls, no address book.' },
  { value: 'class', label: 'Classroom', description: 'Desk phones only: do not disturb follows the school day, with staff and emergency calls still getting through.' }
];

/** Every extension type Stitch can set: people's feature sets and the places' own. */
export const ALL_FEATURE_SETS: readonly FieldOption[] = [...FEATURE_SETS, ...PLACE_FEATURE_SETS];

/** A shared phone's kind (3067): what the place is for, and the extension type that gives it. */
export type SharedPhoneKind = 'full' | 'basic' | 'classroom' | 'hotdesk';

export const SHARED_PHONE_KINDS: readonly { kind: SharedPhoneKind; featureSet: string; label: string; description: string }[] = [
  { kind: 'basic', featureSet: 'hotel', label: 'Basic', description: 'Lobby, kitchen or break room: calls inside the company and basic outside calls, at a lower price. Desk phones only.' },
  { kind: 'full', featureSet: '', label: 'Full', description: 'Conference room or shared office: calls anywhere, like a person’s phone.' },
  { kind: 'classroom', featureSet: 'class', label: 'Classroom', description: 'A school’s classroom: do not disturb follows the school day, and staff and emergency calls still get through. Desk phones only.' },
  { kind: 'hotdesk', featureSet: 'hotdesk', label: 'Hot desk', description: 'A desk people sign in to with their own extension; on its own it calls emergency numbers only.' }
];

/** The extension type a shared phone of this kind gets. */
export const sharedPhoneFeatureSet = (kind: SharedPhoneKind): string => SHARED_PHONE_KINDS.find((k) => k.kind === kind)?.featureSet ?? '';

/** What a shared phone's kind should be from its extension type (Full for a person's type). */
export const sharedPhoneKindOf = (featureSet: string): SharedPhoneKind => SHARED_PHONE_KINDS.find((k) => k.featureSet === featureSet)?.kind ?? 'full';

/**
 * Whether an extension's type on the phone system fits a shared phone's kind. Full fits any person's
 * type that makes calls (Standard, Call queue agent, Apps only); the others need their own type.
 */
export const sharedPhoneTypeFits = (kind: SharedPhoneKind, featureSet: string): boolean =>
  kind === 'full' ? !['mailbox', 'hotdesk', ...PLACE_FEATURE_SETS.map((o) => o.value)].includes(featureSet) : featureSet === sharedPhoneFeatureSet(kind);

export const sharedPhoneKindLabel = (kind: SharedPhoneKind): string => SHARED_PHONE_KINDS.find((k) => k.kind === kind)?.label ?? 'Full';

/**
 * What a mailbox does with old messages (the phone system's `mb_policy`, from its mailbox page; HAR
 * Oct 30 2026). '' leaves it to the company's setting, then the phone system's. Stitch's standard
 * since 1.12.33 (Teampace 2977) is "room": the oldest go only when the box is full.
 */
export const MAILBOX_POLICIES: readonly FieldOption[] = [
  { value: '', label: 'Company default', description: 'Whatever the company’s phone system is set to.' },
  { value: 'room', label: 'Make room for new messages', description: 'The oldest messages go only when the mailbox is full, so new ones always fit.' },
  { value: 'never', label: 'Never delete', description: 'Nothing is deleted. A full mailbox takes no new messages.' },
  { value: '1', label: 'After 1 day' },
  { value: '2', label: 'After 2 days' },
  { value: '3', label: 'After 3 days' },
  { value: '4', label: 'After 4 days' },
  { value: '5', label: 'After 5 days' },
  { value: '6', label: 'After 6 days' },
  { value: '7', label: 'After 1 week' },
  { value: '14', label: 'After 2 weeks' },
  { value: '21', label: 'After 3 weeks' },
  { value: '28', label: 'After 4 weeks' },
  { value: '35', label: 'After 5 weeks' },
  { value: '42', label: 'After 6 weeks' },
  { value: '49', label: 'After 7 weeks' },
  { value: '56', label: 'After 8 weeks' },
  { value: '63', label: 'After 9 weeks' },
  { value: '70', label: 'After 10 weeks' },
  { value: '140', label: 'After 20 weeks' },
  { value: '210', label: 'After 30 weeks' }
];

/** Stitch's standard mailbox policy (1.12.33): set on every mailbox by Stitch Admin → Updates, and on new ones. */
export const STANDARD_MAILBOX_POLICY = 'room';

/** The feature set a person needs to take calls from a call queue. */
export const AGENT_FEATURE_SET = 'agent';
/** A mailbox with no phone: listed as a voicemail box, not a person. */
export const MAILBOX_FEATURE_SET = 'mailbox';

/** Keys that start dial-by-name in an auto attendant. */
const NAME_KEYS: readonly FieldOption[] = [
  { value: '', label: 'Off' },
  ...['9', '8', '0', '#', '*'].map((k) => ({ value: k, label: `Press ${k}` }))
];

const DAYS: readonly [string, string][] = [
  ['mon', 'Monday'],
  ['tue', 'Tuesday'],
  ['wed', 'Wednesday'],
  ['thu', 'Thursday'],
  ['fri', 'Friday'],
  ['sat', 'Saturday'],
  ['sun', 'Sunday']
];

const AFTER_HOURS: FieldDef[] = [
  {
    name: 'afterHoursRule',
    label: 'Time of day rule',
    noun: 'time of day rule',
    type: 'rule',
    section: 'After hours',
    help: 'While this rule is on, calls go to the after-hours destination instead.',
    provisional: true
  },
  {
    name: 'afterHoursDestination',
    label: 'While the rule is on, send calls to',
    noun: 'after-hours destination',
    type: 'destination',
    section: 'After hours',
    provisional: true
  }
];

export const CATALOG: Readonly<Record<AccountKind, KindDef>> = {
  person: {
    kind: 'person',
    singular: 'person',
    plural: 'people',
    title: 'People',
    nameField: '',
    removeCaveat: 'Undo brings back the extension and its settings, but not its voicemail messages, call history or phone sign-in.',
    fields: [
      { name: 'firstName', label: 'First name', noun: 'first name', type: 'text', required: true },
      { name: 'lastName', label: 'Last name', noun: 'last name', type: 'text', required: true },
      { name: 'email', label: 'Email', noun: 'email', type: 'email' },
      { name: 'mobile', label: 'Mobile', noun: 'mobile number', type: 'phone' },
      {
        name: 'featureSet',
        label: 'Feature set',
        noun: 'feature set',
        type: 'choice',
        options: ALL_FEATURE_SETS,
        help: 'What this extension can do. People added to a call queue are switched to Call queue agent automatically.',
        provisional: true
      },
      {
        name: 'timeZone',
        label: 'Time zone',
        noun: 'time zone',
        type: 'pbxChoice',
        optionsFrom: 'timezone',
        help: 'For the times on their voicemail, call history and desk phone clock. Default follows the company’s phone system.',
        provisional: true
      },
      // The settings people change most, from the extension's general and redirection pages.
      // Each waits for confirmation on the phone system before it can change (and shows only
      // where the phone system has it).
      {
        name: 'callerId',
        label: 'Caller ID for their calls out',
        noun: 'caller ID',
        type: 'phone',
        section: 'Phone numbers',
        help: 'The number people see when they call out. Leave empty for the company’s usual number.',
        provisional: true
      },
      { name: 'dnd', label: 'Do not disturb', noun: 'do not disturb', type: 'toggle', section: 'Calls', help: 'Calls go straight to voicemail (or where no-answer calls go).', provisional: true, sentence: 'is on do not disturb' },
      { name: 'forwardAll', label: 'Forward all calls to', noun: 'forwarding of all calls', type: 'destination', section: 'Calls', help: 'Every call goes here instead. Someone in the company, or any phone number.', provisional: true },
      { name: 'forwardBusy', label: 'When they’re on a call, send calls to', noun: 'busy forwarding', type: 'destination', section: 'Calls', provisional: true },
      { name: 'forwardNoAnswer', label: 'When they don’t answer, send calls to', noun: 'no-answer forwarding', type: 'destination', section: 'Calls', help: 'Leave empty for their voicemail.', provisional: true },
      { name: 'noAnswerSeconds', label: 'Ring them for', noun: 'ring time', type: 'seconds', section: 'Calls', help: 'Before calls go to voicemail (or where no-answer calls go). Empty: the phone system’s usual time.', provisional: true },
      // Set with a call of its own (the admin GUI's "Set location"), read back from the extension.
      {
        name: 'emergencyLocation',
        label: 'Emergency address (911)',
        noun: 'emergency address',
        type: 'pbxChoice',
        optionsFrom: 'location',
        section: 'Emergency calls',
        help: 'Where emergency services are sent when they call 911 from their phone. One of the company’s saved addresses.',
        provisional: true
      },
      // 1.12.33 (Teampace 2977): set on user_settings like the voicemail email settings (verified).
      {
        name: 'mailboxPolicy',
        label: 'Old voicemail messages',
        noun: 'what happens to old voicemail',
        type: 'choice',
        options: MAILBOX_POLICIES,
        section: 'Voicemail',
        // Not provisional: written with updateExtension, the call already verified for these settings.
        help: 'Make room for new messages deletes the oldest only when the mailbox is full, so new voicemail always gets through.'
      },
      // Read and saved with calls of their own (the person's greetings), on My phone, their page and
      // Voicemail boxes (1.12.33): the whole greetings state, so undo puts back exactly what was there.
      {
        name: 'voicemailGreeting',
        label: 'Voicemail greeting',
        noun: 'voicemail greeting',
        type: 'greeting',
        section: 'Voicemail',
        separate: true,
        help: 'What callers hear before they leave a message: the standard greeting, a recording, or words typed in Stitch that the phone system speaks.'
      }
    ]
  },
  ringGroup: {
    kind: 'ringGroup',
    singular: 'ring group',
    plural: 'ring groups',
    title: 'Ring Groups',
    nameField: 'name',
    removeCaveat: 'Undo brings back the ring group and its settings, but not its call history.',
    fields: [
      NAME,
      { name: 'stage1', label: 'Ring first', noun: 'first ring stage', type: 'members', section: 'Who rings', help: 'Extensions or numbers that ring together.' },
      { name: 'stage1Seconds', label: 'Ring for', noun: 'first stage ring time', type: 'seconds', section: 'Who rings' },
      { name: 'stage2', label: 'Then ring', noun: 'second ring stage', type: 'members', section: 'Who rings' },
      { name: 'stage2Seconds', label: 'Ring for', noun: 'second stage ring time', type: 'seconds', section: 'Who rings' },
      { name: 'stage3', label: 'Then ring', noun: 'third ring stage', type: 'members', section: 'Who rings' },
      { name: 'stage3Seconds', label: 'Ring for', noun: 'third stage ring time', type: 'seconds', section: 'Who rings' },
      {
        name: 'finalDestination',
        label: 'If nobody answers, send calls to',
        noun: 'no-answer destination',
        type: 'destination',
        section: 'Who rings',
        help: 'A voicemail box, auto attendant, queue or phone number.'
      },
      ...AFTER_HOURS,
      {
        name: 'ringStyle',
        label: 'In each step, ring',
        noun: 'ring style',
        type: 'choice',
        section: 'How it rings',
        more: true,
        provisional: true,
        options: [
          { value: 'all', label: 'Everyone at once', description: 'The first to pick up gets the call.' },
          { value: 'idle', label: 'One person: whoever has been free longest', description: 'Spreads calls evenly across the team.' }
        ]
      },
      {
        name: 'stage1Missed',
        label: 'Show a missed call when the first step stops ringing',
        noun: 'missed-call mark after the first step',
        type: 'toggle',
        section: 'How it rings',
        more: true,
        provisional: true,
        help: 'Phones that rang show a missed call when the group moves on or gives up. Not every phone shows it.',
        sentence: 'shows a missed call when the first step stops ringing'
      },
      { name: 'ringBusy', label: 'Ring people who are already on a call', noun: 'ringing busy people', type: 'toggle', section: 'How it rings', more: true, provisional: true, help: 'Off: busy people are skipped.', sentence: 'rings people who are already on a call' },
      { name: 'ringMobiles', label: 'Ring their mobile phones too', noun: 'ringing mobile phones', type: 'toggle', section: 'How it rings', more: true, provisional: true, help: 'For people who have a mobile number set up.', sentence: 'rings mobile phones too' },
      { name: 'missedCallEmail', label: 'Email missed calls to', noun: 'missed-call email', type: 'email', section: 'Notifications', more: true, provisional: true, help: 'Someone gets an email whenever a call to this group isn’t answered.' },
      { name: 'recordCalls', label: 'Record calls to this group', noun: 'call recording', type: 'toggle', section: 'Notifications', more: true, provisional: true, help: 'Recordings appear with the call in the phone system. Check the rules on recording calls where you are.', sentence: 'records calls' },
      HOLD_MUSIC
    ]
  },
  callQueue: {
    kind: 'callQueue',
    singular: 'call queue',
    plural: 'call queues',
    title: 'Call Queues',
    nameField: 'name',
    removeCaveat: 'Undo brings back the queue and its settings, but not its call history or statistics.',
    presets: [
      {
        id: 'busy-support',
        label: 'Busy support line',
        description: 'Spread calls evenly, give agents 30 seconds after each call, tell callers where they stand.',
        values: { ringOrder: 'idle', wrapUpSeconds: '30', announcePosition: 'true', announceWait: 'true', ringBusyAgents: 'false', ringback: 'music' }
      },
      {
        id: 'small-team',
        label: 'Small team',
        description: 'Ring in the order of the agent list, no break between calls, ringing instead of music.',
        values: { ringOrder: 'order', wrapUpSeconds: '0', announcePosition: 'false', announceWait: 'false', ringback: 'ringing' }
      },
      {
        id: 'sales',
        label: 'Sales line',
        description: 'Never keep a caller waiting: also ring agents already on a call, and their mobiles.',
        values: { ringOrder: 'idle', wrapUpSeconds: '0', ringBusyAgents: 'true', ringMobiles: 'true', announcePosition: 'false' }
      }
    ],
    fields: [
      NAME,
      { name: 'agents', label: 'Agents', noun: 'agents', type: 'members', section: 'Agents', help: 'Extensions that answer calls from this queue.' },
      {
        name: 'loggedInAgents',
        label: 'Logged in now',
        noun: 'logged-in agents',
        type: 'members',
        section: 'Agents',
        live: true,
        provisional: true,
        help: 'Agents who are taking calls right now. Agents can also sign in and out from their phones.'
      },
      { name: 'greeting', label: 'Welcome message', noun: 'welcome message', type: 'speech', section: 'Callers hear', help: 'Read out to callers when they join the queue.' },
      { name: 'announcePosition', label: 'Tell callers their place in line', noun: 'place-in-line announcement', type: 'toggle', section: 'Callers hear', provisional: true },
      { name: 'announceWait', label: 'Tell callers the expected wait', noun: 'expected-wait announcement', type: 'toggle', section: 'Callers hear', provisional: true },
      { name: 'maxCallers', label: 'Most callers waiting at once', noun: 'caller limit', type: 'count', section: 'Limits', provisional: true },
      { name: 'maxWaitSeconds', label: 'Longest wait', noun: 'longest wait', type: 'seconds', section: 'Limits', provisional: true },
      { name: 'maxWaitDestination', label: 'Then send callers to', noun: 'overflow destination', type: 'destination', section: 'Limits', provisional: true },
      {
        name: 'ringOrder',
        label: 'Next call goes to',
        noun: 'ring order',
        type: 'choice',
        section: 'Who gets the next call',
        more: true,
        provisional: true,
        options: [
          { value: 'idle', label: 'Whoever has been free longest', description: 'Spreads calls evenly. The usual choice.' },
          { value: 'order', label: 'In the order of the agent list', description: 'The first agent on the list gets calls first.' },
          { value: 'random', label: 'Anyone, at random', description: 'No pattern.' },
          { value: 'skills', label: 'By skill', description: 'Matches callers to agents’ skills. Ask Stitch to set up skills.' }
        ]
      },
      {
        name: 'wrapUpSeconds',
        label: 'Break after each call',
        noun: 'wrap-up time',
        type: 'seconds',
        section: 'Who gets the next call',
        more: true,
        provisional: true,
        help: 'Time to finish notes before the next queue call. During it, the agent gets no calls from any queue.'
      },
      { name: 'ringBusyAgents', label: 'Ring agents who are already on a call', noun: 'ringing busy agents', type: 'toggle', section: 'Who gets the next call', more: true, provisional: true, help: 'They hear a call-waiting tone.', sentence: 'rings agents who are already on a call' },
      { name: 'ringMobiles', label: 'Ring agents’ mobile phones too', noun: 'ringing mobile phones', type: 'toggle', section: 'Who gets the next call', more: true, provisional: true, sentence: 'rings agents’ mobile phones too' },
      {
        name: 'ringback',
        label: 'While an agent’s phone rings, callers hear',
        noun: 'what callers hear while it rings',
        type: 'choice',
        section: 'Sound',
        more: true,
        provisional: true,
        options: [
          { value: 'ringing', label: 'Ringing' },
          { value: 'music', label: 'Hold music' }
        ]
      },
      HOLD_MUSIC,
      { name: 'minAgents', label: 'Agents who must stay signed in', noun: 'minimum signed-in agents', type: 'count', section: 'Agent sign-in', more: true, provisional: true, help: 'The last agents can’t sign out below this number, so the queue is never left empty.' },
      { name: 'logoutAtMidnight', label: 'Sign everyone out at midnight', noun: 'midnight sign-out', type: 'toggle', section: 'Agent sign-in', more: true, provisional: true, sentence: 'signs everyone out at midnight' },
      { name: 'logoutWhenMissed', label: 'Sign out agents who miss a call', noun: 'sign-out after a missed call', type: 'toggle', section: 'Agent sign-in', more: true, provisional: true, help: 'So calls stop going to someone who has stepped away.', sentence: 'signs out agents who miss a call' }
    ]
  },
  autoAttendant: {
    kind: 'autoAttendant',
    singular: 'auto attendant',
    plural: 'auto attendants',
    title: 'Auto Attendants',
    nameField: 'name',
    removeCaveat: 'Undo brings back the auto attendant and its settings, but not its call history. A recorded greeting comes back only if recordings can be managed here.',
    fields: [
      NAME,
      { name: 'greeting', label: 'Greeting', noun: 'greeting', type: 'speech', section: 'Menu', help: 'Read out when the call is answered, for example "For sales press 1".' },
      {
        name: 'greetingRecording',
        label: 'Recorded greeting',
        noun: 'recorded greeting',
        type: 'recording',
        section: 'Menu',
        provisional: true,
        help: 'Plays instead of the spoken text.'
      },
      { name: 'menu', label: 'Menu options', noun: 'menu', type: 'menu', section: 'Menu', help: 'Up to 10 keys. Callers can also dial an extension directly.' },
      { name: 'timeoutSeconds', label: 'Wait for a key press', noun: 'key-press wait', type: 'seconds', section: 'No key pressed', provisional: true },
      { name: 'timeoutDestination', label: 'If no key is pressed, send calls to', noun: 'no-key destination', type: 'destination', section: 'No key pressed', provisional: true },
      ...AFTER_HOURS,
      { name: 'dialByName', label: 'Find someone by name', noun: 'dial by name', type: 'choice', options: NAME_KEYS, section: 'Caller options', more: true, provisional: true, help: 'Callers press this key, then spell a name on the keypad.' },
      { name: 'hangupSeconds', label: 'Hang up after this long with nothing pressed', noun: 'hang-up time', type: 'seconds', section: 'Caller options', more: true, provisional: true, help: 'Clears silent calls so they don’t tie up a line.' },
      HOLD_MUSIC
    ]
  },
  timeRule: {
    kind: 'timeRule',
    singular: 'time of day rule',
    plural: 'time of day rules',
    title: 'Time of Day Rules',
    nameField: 'name',
    removeCaveat: 'Undo brings back the rule and its hours. A rule still used for after hours can’t be removed until that’s changed.',
    fields: [
      NAME,
      {
        name: 'mode',
        label: 'How it switches',
        noun: 'switching',
        type: 'choice',
        options: [
          { value: 'schedule', label: 'By the hours below' },
          { value: 'manual', label: 'By hand' }
        ]
      },
      {
        name: 'manualState',
        label: 'Set by hand to',
        noun: 'manual setting',
        type: 'choice',
        options: [
          { value: 'on', label: 'On' },
          { value: 'off', label: 'Off' }
        ],
        showWhen: { field: 'mode', values: ['manual'] }
      },
      {
        name: 'hoursMeaning',
        label: 'During these hours the rule is',
        noun: 'meaning of the hours',
        type: 'choice',
        section: 'Hours',
        options: [
          { value: 'off', label: 'Off (on outside them, for example after hours)' },
          { value: 'on', label: 'On' }
        ],
        provisional: true
      },
      ...DAYS.map(([name, label]): FieldDef => ({ name, label, noun: `${label} hours`, type: 'hours', section: 'Hours' })),
      {
        name: 'holidays',
        label: 'Holidays and special days',
        noun: 'holidays',
        type: 'holidays',
        section: 'Holidays',
        provisional: true,
        help: 'Closed all day, or special hours, on these dates.'
      },
      { name: 'timezone', label: 'Time zone', noun: 'time zone', type: 'pbxChoice', optionsFrom: 'timezone', section: 'Settings', more: true, provisional: true, help: 'Leave on Default to use your account’s time zone.' },
      { name: 'pin', label: 'PIN to switch it by phone', noun: 'PIN', type: 'digits', section: 'Settings', more: true, provisional: true, help: 'Anyone who dials the rule’s number can switch it with this PIN, even from outside the office.' }
    ]
  },
  callPark: {
    kind: 'callPark',
    singular: 'park orbit',
    plural: 'park orbits',
    title: 'Call Park',
    nameField: 'name',
    removeCaveat: 'Undo brings back the orbit and its settings. A call parked on it at the time is not brought back.',
    fields: [
      // Park orbits keep their name in a field also called `name`; confirm it never renames the orbit.
      { ...NAME, provisional: true },
      { name: 'reminderSeconds', label: 'Remind whoever parked the call after', noun: 'reminder time', type: 'seconds' },
      { name: 'reminderDestination', label: 'After the reminder, send the call to', noun: 'reminder destination', type: 'destination' },
      HOLD_MUSIC
    ]
  },
  phone: {
    kind: 'phone',
    singular: 'phone',
    plural: 'phones',
    title: 'Phones',
    nameField: '',
    removeCaveat: 'The phone stops getting its settings from the phone system. Undo brings it back with its keys.',
    fields: [
      { name: 'model', label: 'Model', noun: 'model', type: 'phoneModel', required: true, options: PHONE_MODEL_OPTIONS },
      { name: 'user', label: 'Whose phone', noun: 'person it belongs to', type: 'person', help: 'Their line and keys go on this phone. Leave empty for a spare.' },
      { name: 'keys', label: 'Keys', noun: 'keys', type: 'keys', help: 'What each programmable key does.' }
    ]
  },
  conference: {
    // Stitch's own: the hidden presence room busy lamps call while someone is in a Patch from the
    // app (1.12.18). Never shown to customers. No PIN and no prompts.
    kind: 'conference',
    singular: 'conference room',
    plural: 'conference rooms',
    title: 'Conference Rooms',
    nameField: 'name',
    removeCaveat: 'Undo brings back the room and its settings.',
    fields: [
      NAME,
      // From the admin GUI's HAR (Oct 31, 2026): a room's settings are user_settings, its kind is
      // `type` ("open" is what the admin calls an Ad-hoc conference). There's no PIN setting.
      { name: 'roomType', label: 'Type', noun: 'type of room', type: 'choice', options: [{ value: 'open', label: 'Ad-hoc conference' }], help: 'Ad-hoc: anyone can dial in at any time.' }
    ]
  },
  number: {
    kind: 'number',
    singular: 'phone number',
    plural: 'phone numbers',
    title: 'Numbers',
    nameField: '',
    removeCaveat: '',
    fields: [
      {
        name: 'destination',
        label: 'Calls go to',
        noun: 'destination',
        type: 'account',
        required: true,
        help: 'A person, voicemail box, auto attendant, ring group, call queue or time of day rule in this company.'
      }
    ]
  }
};

/**
 * Trigger numbers Stitch uses to route calls between its phone platforms: anything in +1 899.
 * Callers can't dial them and they're never anyone's caller ID, so Stitch shows them as
 * "internal routes", apart from the company's real numbers, and only Stitch staff change them.
 */
export function isInternalRoute(n: string | null | undefined): boolean {
  const d = (n ?? '').replace(/\D/g, '');
  return (d.startsWith('1899') && d.length >= 11) || (d.startsWith('899') && d.length >= 10);
}

/** "Internal route 0142": the last four digits tell several apart. */
export function internalRouteLabel(n: string): string {
  return `Internal route ${n.replace(/\D/g, '').slice(-4)}`;
}

/** A phone number the way people read it: (410) 555-0100 for North American numbers, otherwise as given. */
export function formatPhoneNumber(n: string): string {
  if (isInternalRoute(n)) return internalRouteLabel(n);
  const d = n.replace(/[^\d+]/g, '');
  const m = /^\+?1?(\d{3})(\d{3})(\d{4})$/.exec(d);
  return m && (d.startsWith('+1') || d.length === 10 || (d.length === 11 && d.startsWith('1'))) ? `(${m[1]}) ${m[2]}-${m[3]}` : n;
}

/** A phone number as the phone system keys it: "+" and digits, separators removed. Null when it isn't one. */
export function normalizePhoneNumber(n: string): string | null {
  const d = n.trim().replace(/[\s().-]/g, '');
  return /^\+?\d{7,15}$/.test(d) ? d : null;
}

/** "a ring group", "an auto attendant". */
export function withArticle(noun: string): string {
  return `${/^[aeiou]/i.test(noun) ? 'an' : 'a'} ${noun}`;
}

export function kindDef(kind: AccountKind): KindDef {
  return CATALOG[kind];
}

export function isAccountKind(v: unknown): v is AccountKind {
  return typeof v === 'string' && (ACCOUNT_KINDS as readonly string[]).includes(v);
}

export function fieldDef(kind: AccountKind, field: string): FieldDef | undefined {
  return CATALOG[kind].fields.find((f) => f.name === field);
}

// --- values ------------------------------------------------------------------------------

/** Neutral settings of one account: field name -> value ("" = not set). */
export type FieldValues = Record<string, string>;

const DIAL = /^[0-9*#+][0-9*#]{0,31}$/;
// Letters, digits and the usual punctuation before the @; a real host name after it. No quotes,
// spaces or angle brackets: these addresses go into emails the PBX sends.
const EMAIL = /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/;
const MENU_KEY = /^[0-9*#]{1,4}$/;
const TIME = '(?:[01]?\\d|2[0-4])(?::[0-5]\\d)?(?:[AP]M?)?';
const RANGE = new RegExp(`^${TIME}-${TIME}$`, 'i');

const split = (v: string) => v.split(/[\s,;]+/).filter(Boolean);

/** An uploaded recording, by the SHA-256 of its audio. */
export const RECORDING_REF = /^sha256:[0-9a-f]{64}$/;
/** Values from the phone system's own lists (hold music ids, time zone codes). */
const PBX_OPTION = /^[A-Za-z0-9_./+:-]{1,40}$/;

/**
 * Compare values the way the PBX means them, so its own formatting is never mistaken for
 * someone else's edit: emails ignore case, phone numbers and lists ignore separators, numbers
 * ignore leading zeros, unset toggles read as off.
 */
export function normValue(type: FieldType | undefined, value: string | undefined): string {
  const t = (value ?? '').trim();
  switch (type) {
    case 'email':
      return t.toLowerCase();
    case 'phone':
      return t.replace(/[\s().-]/g, '');
    case 'members':
      return split(t).join(' ');
    case 'seconds':
    case 'count':
      return /^\d+$/.test(t) ? String(Number(t)) : t;
    case 'toggle':
      // Only "not set" reads as off. Anything unexpected stays as it is, so it never compares
      // equal to a value we meant (a mismatch rolls back; a false match would hide a failure).
      return t === '' ? 'false' : t.toLowerCase();
    case 'choice':
      return t.toLowerCase();
    case 'hours':
      return t.replace(/\s+/g, ' ').toUpperCase();
    case 'menu':
      return parseMenu(t)
        .map((o) => `${o.key}:${o.destination}`)
        .join(' ');
    case 'holidays': {
      const list = t.startsWith('raw:') ? null : parseHolidays(t);
      return list === null ? t : formatHolidays(list);
    }
    case 'recording':
      return t.toLowerCase();
    case 'phoneModel':
      return t.toLowerCase();
    case 'keys':
      return formatKeys(parseKeys(t));
    case 'greeting': {
      const g = parseGreetings(t);
      return g ? formatGreetings(g) : t;
    }
    default:
      return t;
  }
}

export function normField(kind: AccountKind, field: string, value: string | undefined): string {
  return normValue(fieldDef(kind, field)?.type, value);
}

export interface MenuOption {
  key: string;
  destination: string;
}

/** "1:201 2:300" -> [{key: "1", destination: "201"}, ...]. Lenient; validation is separate. */
export function parseMenu(value: string): MenuOption[] {
  return split(value).map((pair) => {
    const i = pair.indexOf(':');
    return i < 0 ? { key: pair, destination: '' } : { key: pair.slice(0, i), destination: pair.slice(i + 1) };
  });
}

export function formatMenu(options: readonly MenuOption[]): string {
  return options
    .filter((o) => o.key.trim() !== '' || o.destination.trim() !== '')
    .map((o) => `${o.key.trim()}:${o.destination.trim()}`)
    .join(' ');
}

/** Plain-language problem with a value, or null. Empty values are checked by `required` only. */
export function checkValue(def: FieldDef, value: string): string | null {
  const v = value.trim();
  if (v === '') return null;
  switch (def.type) {
    case 'text':
      return v.length > 64 ? 'At most 64 characters' : null;
    case 'email':
      return EMAIL.test(v) && v.length <= 254 ? null : 'Not a valid email address';
    case 'phone':
      return /^\+?[0-9 ().-]{3,24}$/.test(v) ? null : 'Not a valid phone number';
    case 'seconds':
      return /^\d{1,5}$/.test(v) && Number(v) <= 86_400 ? null : 'Enter a number of seconds';
    case 'count':
      return /^\d{1,4}$/.test(v) ? null : 'Enter a whole number';
    case 'destination':
    case 'rule':
      return DIAL.test(v) ? null : 'Enter an extension or phone number';
    case 'members': {
      const items = split(v);
      if (items.length > 100) return 'At most 100 entries';
      const bad = items.find((m) => !DIAL.test(m));
      return bad ? `"${bad}" is not an extension or phone number` : null;
    }
    case 'toggle':
      return v === 'true' || v === 'false' ? null : 'Must be on or off';
    case 'choice':
      return def.options?.some((o) => o.value === v) ? null : `Must be one of: ${(def.options ?? []).map((o) => o.label).join(', ')}`;
    case 'pbxChoice':
      return PBX_OPTION.test(v) ? null : 'Choose one from the list';
    case 'digits':
      return /^\d{1,10}$/.test(v) ? null : 'Use 1 to 10 digits';
    case 'recording':
      return RECORDING_REF.test(v) ? null : 'Upload or record the greeting again';
    case 'holidays':
      return checkHolidays(v);
    case 'hours': {
      const bad = split(v).find((r) => !RANGE.test(r));
      return bad ? `"${bad}" is not a time range like 9:00A-5:00P` : null;
    }
    case 'speech':
      // Recordings are uploaded in the phone system; only text can be typed here.
      if (v.startsWith('file:')) return 'Type the words to say; recordings are managed in the phone system';
      return v.length > 500 ? 'At most 500 characters' : null;
    case 'phoneModel':
      return phoneModel(v) || /^other:[A-Za-z0-9 ._/+-]{1,60}$/.test(v) ? null : 'Choose a phone model';
    case 'person':
      return /^[A-Za-z0-9]{1,32}$/.test(v) ? null : 'Choose a person';
    case 'account':
      return /^[A-Za-z0-9]{1,32}$/.test(v) ? null : 'Choose where calls go';
    case 'keys':
      return checkKeys(v);
    case 'greeting':
      return greetingProblem(v);
    case 'menu': {
      const options = parseMenu(v);
      if (options.length > 10) return 'At most 10 menu options';
      const keys = new Set<string>();
      for (const o of options) {
        if (!MENU_KEY.test(o.key)) return `"${o.key}" is not a key callers can press`;
        if (!DIAL.test(o.destination)) return `Key ${o.key}: enter an extension or phone number`;
        if (keys.has(o.key)) return `Key ${o.key} is used twice`;
        keys.add(o.key);
      }
      return null;
    }
  }
}

/** The account's name as people see it. */
export function displayOf(kind: AccountKind, extension: string, values: FieldValues | null | undefined): string {
  const def = CATALOG[kind];
  if (kind === 'person') {
    const n = `${values?.firstName ?? ''} ${values?.lastName ?? ''}`.trim();
    return n || `extension ${extension}`;
  }
  if (kind === 'phone') {
    const m = values?.model ? phoneModel(values.model) : undefined;
    const label = m ? `${m.vendor} ${m.model}` : values?.model?.startsWith('other:') ? values.model.slice(6) : 'phone';
    return `${label} ${formatMac(extension)}`;
  }
  if (kind === 'number') return formatPhoneNumber(extension);
  const n = (values?.[def.nameField] ?? '').trim();
  return n || `${def.singular} ${extension}`;
}

/**
 * Checks that need more than one setting: a phone's keys must be keys it has. Null when fine.
 * `listed`: the keys the phone system lists for this phone (its button list), when read; they
 * decide, and the model's own count is used only when they aren't known.
 */
export function crossCheck(kind: AccountKind, values: FieldValues, opts: { listed?: readonly string[] } = {}): string | null {
  if (kind === 'phone' && values.keys) return checkKeys(values.keys, values.model ? phoneModel(values.model) : undefined, opts.listed);
  return null;
}

/** Shown in summaries: "(none)" for empty, decoded menus and toggles. */
export function showValue(kind: AccountKind, field: string, value: string | undefined): string {
  const def = fieldDef(kind, field);
  const v = (value ?? '').trim();
  if (def?.type === 'toggle') return v === 'true' ? 'on' : 'off';
  const option = def?.type === 'choice' ? def.options?.find((o) => o.value === v) : undefined;
  if (option) return option.label.split(' (')[0]?.toLowerCase() ?? v;
  if (def?.type === 'pbxChoice' && v === '') return 'default';
  if (v === '') return '(none)';
  if (def?.type === 'speech') return v.startsWith('file:') ? 'a recording' : `"${v.length > 40 ? v.slice(0, 40) + '…' : v}"`;
  if (def?.type === 'menu') return parseMenu(v).map((o) => `${o.key}→${o.destination}`).join(', ');
  if (def?.type === 'recording') return 'a new recording';
  if (def?.type === 'phoneModel') {
    const m = phoneModel(v);
    return m ? `${m.vendor} ${m.model}` : v.replace(/^other:/, '');
  }
  if (def?.type === 'keys') return `${parseKeys(v).length} keys set`;
  if (def?.type === 'digits') return '••••';
  if (def?.type === 'greeting') return describeGreetings(v);
  if (def?.type === 'holidays') {
    const list = parseHolidays(v);
    return list === null ? 'holidays set in the phone system' : `${list.length} holiday${list.length === 1 ? '' : 's'}`;
  }
  return v;
}

/** A full value set for a kind, "" for anything missing. */
export function completeValues(kind: AccountKind, input: Partial<FieldValues>, fields?: readonly string[]): FieldValues {
  const out: FieldValues = {};
  for (const f of CATALOG[kind].fields) {
    if (fields && !fields.includes(f.name)) continue;
    out[f.name] = (input[f.name] ?? '').trim();
  }
  return out;
}

// --- live state and call records ----------------------------------------------------------

/** What is happening on an account right now, when the PBX reports it. Read-only. */
export interface AccountLive {
  /** Calls in progress on this account. */
  calls?: number;
  /** Queue agents currently logged in. */
  loggedIn?: string[];
  /** Time of day rule: on right now. */
  on?: boolean;
  /** Phones: what the phone system knows about the device itself. */
  phone?: {
    /** Signed in to the phone system right now (any line on it). */
    online: boolean;
    /** What the phone reports about itself when signed in ("Yealink SIP-T54W 96.86.0.70"). */
    userAgent?: string;
    firmware?: string;
    /** More lines on the phone after its owner's (set in the phone system). */
    extraLines?: string[];
    /**
     * Every key the phone system lists for this phone (its button list: line keys and expansion
     * module keys, set or not), as Stitch names them ("L1"…"L84", "E1-1"…), when its keys were
     * read. These are the keys that exist: a key outside them is refused, and none of them is.
     */
    slots?: string[];
  };
  /** Phone numbers: how the phone system shows it, when that isn't just the number. */
  number?: { label: string };
  /** People, from the phone system's list of extensions: do not disturb, and phones signed in. */
  presence?: { dnd: boolean; registrations: number };
}

export interface Account {
  kind: AccountKind;
  tenant: string;
  extension: string;
  values: FieldValues;
  /**
   * Opaque hash of the whole record as the PBX stores it, including settings the portal does
   * not model. Any change to the account changes it, which is how undo avoids removing an
   * account someone has since configured.
   */
  fingerprint?: string;
  live?: AccountLive;
}

/** Which kinds this PBX can manage, and how far. */
export interface KindSupport {
  kind: AccountKind;
  /** Fields this PBX stores for the kind. Others are hidden and refused. */
  fields: readonly string[];
  /**
   * Fields that can be set, when the kind is writable. A setting still being confirmed on a kind
   * that is otherwise confirmed (a person's feature set, say) can be shown but not changed.
   */
  writableFields: readonly string[];
  /** Adding and changing are known to work on this PBX build. */
  writable: boolean;
  /** Removing is known to work on this PBX build. */
  removable: boolean;
  /** Fields only read when asked for (they cost extra requests). */
  lazyFields?: readonly string[];
}

export interface CallParty {
  name: string;
  number: string;
}

export interface CallRecord {
  id: string;
  /** The phone system's own number for this record, for opening the call in full. */
  recordId?: string;
  /** ISO time the call started. */
  start: string;
  /** ISO time it was answered, if it was. */
  answered?: string;
  /** ISO time it ended. */
  end?: string;
  durationSeconds?: number;
  direction: 'inbound' | 'outbound' | 'internal' | 'unknown';
  from: CallParty;
  to: CallParty;
  /** Extensions, queues or groups the call went through, when the PBX says. */
  via: string[];
  /** True when the caller hung up before anyone answered. */
  missed: boolean;
  /**
   * One of Stitch's own Patch calls (1.12.18): a desk phone rung for a Patch ("Patch ring: Ann →
   * Dana", its legs folded into one) or a 1-on-1 Patch's call ("Patch call: Ann → Dana"). Shown
   * as this line instead of the numbers.
   */
  patch?: { kind: 'ring' | 'call'; text: string };
}

/** How a call sounded, from the phone system's quality reports. */
export interface CallQualityReading {
  /** Mean opinion score, 1 (bad) to 5 (excellent). */
  mos?: number;
  /** Packets lost on the network, percent. */
  lossPercent?: number;
  /** Jitter (variation in packet arrival), milliseconds. */
  jitterMs?: number;
  /** Round-trip delay, milliseconds. */
  delayMs?: number;
}

/** A plain-words rating of a MOS score: 4.0 and up excellent, 3.6 good, 3.1 fair, below that poor. */
export type QualityRating = 'excellent' | 'good' | 'fair' | 'poor';

export function qualityRating(mos: number): QualityRating {
  if (mos >= 4) return 'excellent';
  if (mos >= 3.6) return 'good';
  if (mos >= 3.1) return 'fair';
  return 'poor';
}

/** One side of a call: the carrier's trunk, or a phone it rang. */
export interface CallLeg {
  kind: 'trunk' | 'phone';
  /** Seen from the phone system: into it (from the caller) or out of it (to a phone or carrier). */
  direction: 'inbound' | 'outbound' | 'unknown';
  start: string;
  answered?: string;
  end?: string;
  /** Who it rang, for phone legs; whose trunk it used, for trunk legs. */
  extension?: string;
  extensionName?: string;
  trunk?: string;
  from: CallParty;
  to: CallParty;
  /** Who the call was redirected from (forwarding), when it was. */
  redirectedFrom?: CallParty;
  /** SIP final response, when the leg ended with one (486 busy, 487 cancelled…). */
  sipCode?: number;
  /** The phone or app, as it describes itself ("Yealink SIP-T54W 96.86.0.70"). */
  device?: string;
  /** transport:ip:port the media/signalling came from. */
  address?: string;
  codec?: string;
  /** As measured by the phone system (local) and, when reported, by the other end (remote). */
  quality?: CallQualityReading & { remote?: CallQualityReading };
}

/** A feature the call went through: a queue, auto attendant, ring group. */
export interface CallStep {
  /** callQueue, autoAttendant, ringGroup, or the phone system's own word. */
  kind: string;
  account: string;
  accountName: string;
  /** The agent or person who answered there. */
  answeredBy?: string;
  answeredByName?: string;
  start: string;
  menuSeconds?: number;
  ringSeconds?: number;
  talkSeconds?: number;
  holdSeconds?: number;
  /** How the step ended, as the phone system codes it ("hc": the caller hung up). */
  reason?: string;
}

export interface CallRecording {
  id: string;
  time: string;
  account?: string;
  accountName?: string;
  extension?: string;
  extensionName?: string;
}

/** One SIP message the phone system sent or received for the call. */
export interface SipMessage {
  time: string;
  from: string;
  to: string;
  /** "INVITE sip:…" or "SIP/2.0 200 OK". */
  line: string;
  method?: string;
  status?: number;
  text: string;
}

export interface CallLogLine {
  time: string;
  category: string;
  level: number;
  message: string;
}

/** One call in full: how it moved through the phone system, who it reached, how it sounded. */
export interface CallDetail {
  id: string;
  callId: string;
  start: string;
  answered?: string;
  end?: string;
  durationSeconds?: number;
  direction: CallRecord['direction'];
  from: CallParty;
  to: CallParty;
  missed: boolean;
  legs: CallLeg[];
  steps: CallStep[];
  recordings: CallRecording[];
  /** The worst leg's score, with its rating. */
  quality?: { mos: number; rating: QualityRating };
  sip: SipMessage[];
  log: CallLogLine[];
}

/** A voicemail. */
export interface VoicemailMessage {
  id: string;
  time: string;
  from: CallParty;
  /** The mailbox it's in. */
  mailbox: string;
  /** Who it was left for, when it came through a feature (a queue, a menu). */
  leftAt?: string;
  seconds: number;
  isNew: boolean;
  hasAudio: boolean;
  note: string;
}

export interface Mailbox {
  total: number;
  newCount: number;
  messages: VoicemailMessage[];
}

/** A recorded call from a person's own list. */
export interface RecordedCall {
  id: string;
  time: string;
  from: CallParty;
  to: CallParty;
  seconds: number;
}

/** An emergency (E911) location kept on the phone system. */
export interface EmergencyLocation {
  id: string;
  name: string;
  /** One line: "123 Main St, Suite 4, Baltimore, MD 21201". */
  address: string;
  callback: string;
  active: boolean;
}

export interface CallPage {
  calls: CallRecord[];
  /** Total records for the tenant, when the PBX reports it. */
  total?: number;
  page: number;
  size: number;
}

// --- directory -----------------------------------------------------------------------------

/** One number in a tenant, for pickers and for showing names instead of numbers. */
export interface DirectoryEntry {
  extension: string;
  /** A kind Stitch manages, or "other" (conference rooms, paging groups, voice agents...). */
  kind: AccountKind | 'other';
  name: string;
  /** For "other": what it is, in plain words ("conference room"). */
  label?: string;
  /** For people: their feature set ("" = standard, "agent" = call queue agent...). */
  featureSet?: string;
}

export interface Directory {
  entries: DirectoryEntry[];
  /**
   * Dialling this prefix before a person's extension reaches their voicemail (8 + 201 = 8201).
   * The PBX's default; a tenant can change it.
   */
  mailboxPrefix: string;
}

// --- wallboard ----------------------------------------------------------------------------------

export type AgentState = 'available' | 'busy' | 'dnd' | 'loggedOut';

export interface BoardAgent {
  extension: string;
  name: string;
  state: AgentState;
  /** Queue calls answered today, when the phone system reports it. */
  answeredToday?: number;
  /** Queue calls that rang this agent and weren't answered today. */
  missedToday?: number;
}

export interface BoardCaller {
  /** The phone system's id for the call, for answering it out of turn (2714). */
  id?: string;
  /** The phone system lets someone take this caller ahead of their turn (its `pickup`). */
  canAnswer?: boolean;
  /** Seconds waiting so far (at `Wallboard.at`). */
  waitingSeconds: number;
  /** Caller ID, when the phone system reports it. */
  from?: string;
  /** Ringing an agent right now (rather than waiting in line). */
  ringing?: boolean;
}

/** A queue call being answered right now. */
export interface BoardTalk {
  agent: string;
  /** Seconds since the call came in. */
  seconds: number;
  from?: string;
}

export interface BoardQueue {
  extension: string;
  name: string;
  /** Callers waiting right now. */
  waiting: number;
  /** The caller who has waited longest, in seconds, when the phone system reports it. */
  longestWaitSeconds?: number;
  callers?: BoardCaller[];
  agents: BoardAgent[];
  answeredToday?: number;
  /** Callers who hung up before anyone answered, today. */
  abandonedToday?: number;
  /** Calls sent elsewhere today (longest wait reached, no agents...). */
  redirectedToday?: number;
  /** Every call into the queue today. */
  callsToday?: number;
  /** Calls that rang an agent who didn't answer, today. */
  missedToday?: number;
  /** Average wait before an answer today, in seconds. */
  avgWaitSeconds?: number;
  /** Average talk time today, in seconds. */
  avgTalkSeconds?: number;
  /** Longest wait today, in seconds. */
  maxWaitSeconds?: number;
  /** Calls being answered right now. */
  talking?: BoardTalk[];
}

/** What is happening in the tenant's call queues right now. Read-only. */
export interface Wallboard {
  /** ISO time of the reading. */
  at: string;
  queues: BoardQueue[];
  /** Park orbits with a call on them. */
  parked: { extension: string; name: string; calls: number }[];
  /** Time of day rules and whether each is on. */
  rules: { extension: string; name: string; on: boolean }[];
  /** Parts the phone system couldn't provide this time, in plain words. */
  missing?: string[];
}

// --- Queue reports ----------------------------------------------------------------------------

/**
 * A stretch of time the phone system keeps queue totals for: one hour, one day or one month, in
 * the phone system's own local time. Reports read them in that time zone, and say so (3136).
 */
export type ReportPeriod = { kind: 'hour'; date: string; hour: number } | { kind: 'day'; date: string } | { kind: 'month'; month: string };

/**
 * What a queue (or one agent in it) handled over a period, as the phone system counts it. Durations
 * in milliseconds, kept as sums and counts so periods add up exactly.
 */
export interface QueueTotals {
  /** Calls that came in to the queue (for an agent: calls offered to them). */
  offered: number;
  answered: number;
  /**
   * The phone system's "missed": calls that rang an agent who didn't pick up, as on the wallboard.
   * One call can be missed by one agent and answered by another, so this isn't calls less answered:
   * Reports show Not answered as offered − answered (3136).
   */
  missed: number;
  /** The caller hung up while waiting. */
  abandoned: number;
  /** Answered within the queue's target wait. */
  withinTarget: number;
  waitMs: number;
  waitCount: number;
  maxWaitMs: number;
  talkMs: number;
  talkCount: number;
  maxTalkMs: number;
  holdMs: number;
  holdCount: number;
  /** Agents available, summed over samples (divide by availCount for the average). */
  availSum: number;
  availCount: number;
}

export const NO_TOTALS: QueueTotals = Object.freeze({
  offered: 0,
  answered: 0,
  missed: 0,
  abandoned: 0,
  withinTarget: 0,
  waitMs: 0,
  waitCount: 0,
  maxWaitMs: 0,
  talkMs: 0,
  talkCount: 0,
  maxTalkMs: 0,
  holdMs: 0,
  holdCount: 0,
  availSum: 0,
  availCount: 0
});

/** Adds periods together. */
export function addTotals(a: QueueTotals, b: QueueTotals | null | undefined): QueueTotals {
  if (!b) return a;
  return {
    offered: a.offered + b.offered,
    answered: a.answered + b.answered,
    missed: a.missed + b.missed,
    abandoned: a.abandoned + b.abandoned,
    withinTarget: a.withinTarget + b.withinTarget,
    waitMs: a.waitMs + b.waitMs,
    waitCount: a.waitCount + b.waitCount,
    maxWaitMs: Math.max(a.maxWaitMs, b.maxWaitMs),
    talkMs: a.talkMs + b.talkMs,
    talkCount: a.talkCount + b.talkCount,
    maxTalkMs: Math.max(a.maxTalkMs, b.maxTalkMs),
    holdMs: a.holdMs + b.holdMs,
    holdCount: a.holdCount + b.holdCount,
    availSum: a.availSum + b.availSum,
    availCount: a.availCount + b.availCount
  };
}

